/**
 * Public Login Prompt
 *
 * Shared modal that routes unauthenticated public users to auth.
 *
 * Primary responsibilities:
 * - Offer login and signup actions from public chat surfaces
 * - Keep prompt styling aligned with the platform dialog grammar
 *
 * Dependencies:
 * - components/ui/dialog
 * - components/ui/button
 */

'use client'

import { useRouter } from 'next/navigation'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface PublicLoginPromptProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional feature name used when description is not provided. */
  feature?: string
  description?: string
}

export function PublicLoginPrompt({
  open,
  onOpenChange,
  feature = 'this feature',
  description
}: PublicLoginPromptProps) {
  const router = useRouter()

  const handleLogin = () => {
    router.push(`/login?redirect=/try`)
    onOpenChange(false)
  }

  const handleSignup = () => {
    router.push(`/login?register=true&redirect=/try`)
    onOpenChange(false)
  }

  const body =
    description?.trim() ||
    (feature === 'this feature'
      ? "You'll get smarter responses, saved chats, and higher upload limits."
      : `Log in to use ${feature}.`)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Log in or sign up</DialogTitle>
          <DialogDescription className="pt-2">{body}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-4">
          <div className="space-y-2">
            <Button onClick={handleLogin} className="h-10 w-full" variant="default">
              Log in
            </Button>
            <Button onClick={handleSignup} className="h-10 w-full" variant="outline">
              Sign up for free
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
